import React, {useContext, useState} from "react";
import {Link} from "react-router-dom";
import {GlobalContext} from "../../GlobalReducer";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import {ProductInterface} from "../../interfaces/catalog/ProductInterface";
import Money from "../core/Money";
import ChatProductModal, {openInModal} from "./ChatProductModal";
import {getStockThresholdLabel, hasAvailableStock, shouldShowMskStock} from "../../utils/stockLabel";
import useBasket from "../../utils/useBasket";

interface Props {
    id: number
    href: string // ссылка из ответа — пока данные не пришли или товара нет в каталоге
    label: string
    product?: ProductInterface
    loading: boolean
}

function productLink(product: ProductInterface): string {
    return `/catalog/item/${product.catalog_code}/${product.id}/${product.human_url}`;
}

// Наличие — как в карточке каталога (ui/catalog/Product.tsx): склад клиента и, если он не московский, МСК
function Stock({product}: {product: ProductInterface}) {
    const [state] = useContext<[ReducerStateInterface, Function]>(GlobalContext);

    if (!product.client_price) {
        return <div className={'chat-product__stock ' + (product.has_stock ? 'text-success' : 'text-danger')}>
            {product.has_stock ? 'В наличии ' + product.smart_stock : 'Нет в наличии'}
        </div>;
    }

    const mskCnt = product.base_stock?.cnt_for_sale ?? 0;
    const stockId = state.customer?.stock_id;

    return <>
        <div className={'chat-product__stock ' + (product.has_client_stock ? 'text-success' : 'text-danger')}>
            {product.has_client_stock ? `В наличии (${stockId}) ${product.client_smart_stock}` : `Нет в наличии (${stockId})`}
        </div>
        {shouldShowMskStock(product.base_stock?.stock_id, product.client_stock?.stock_id) &&
            <div className={'chat-product__stock ' + (mskCnt > 0 ? 'text-success' : 'text-danger')}>
                {mskCnt > 0 ? 'В наличии (МСК) ' + getStockThresholdLabel(mskCnt) : 'Нет в наличии (МСК)'}
            </div>}
    </>;
}

function Price({product}: {product: ProductInterface}) {
    const price = product.client_price || product.current_price;
    const oldPrice = product.client_price ? product.client_base_price : product.base_price;

    return <div className="chat-product__prices">
        <div className="chat-product__price">
            {oldPrice && oldPrice.amount > price.amount &&
                <span className="chat-product__old-price"><Money value={oldPrice}/></span>}
            <Money value={price}/>
        </div>
        {product.client_price && product.current_price &&
            <div className="chat-product__rrp">РЦ: <Money value={product.current_price}/></div>}
    </div>;
}

// «В корзину» — как в карточке каталога: только если товар есть на складе клиента или в МСК
function BasketButton({product}: {product: ProductInterface}) {
    const {addToBasket, inBasket} = useBasket(product);

    if (!product.client_price || !hasAvailableStock(product.has_client_stock, product.base_stock?.cnt_for_sale)) {
        return null;
    }

    return <button type="button" className={'btn chat-product__btn ' + (inBasket() ? 'btn-dark disabled' : 'btn-primary')}
                   onClick={addToBasket}>{inBasket() ? 'В корзине' : 'В корзину'}</button>;
}

/**
 * Мини-карточка товара в ответе консультанта: фото, название, артикул и код, цена клиента, РЦ и наличие —
 * на момент показа, кнопка «В корзину». По фото и названию страница товара открывается в модальном окне, чат остаётся на месте.
 */
export default function ChatProductCard({id, href, label, product, loading}: Props) {
    const [open, setOpen] = useState(false);

    if (!product) {
        return <div className="chat-product chat-product--empty">
            {loading ? <span className="text-muted">{label}</span> : <Link to={href}>{label}</Link>}
        </div>;
    }

    const name = product.name.replace(/\u00a0/g, ' ').replace(/&nbsp;/g, ' ');
    const image = 'https://www.pride.ru' + (product.list_image || '/images/no-photo.jpg');

    const show = (event: React.MouseEvent) => openInModal(event, () => setOpen(true));

    return <div className="chat-product">
        <Link to={productLink(product)} onClick={show} className="chat-product__image"><img src={image} alt={name}/></Link>
        <Link to={productLink(product)} onClick={show} className="chat-product__name">{name}</Link>
        <div className="chat-product__ids">Артикул: {product.articul} · Код: {product.code}</div>
        <div className="chat-product__footer">
            <Price product={product}/>
            <Stock product={product}/>
            <BasketButton product={product}/>
        </div>
        <ChatProductModal productId={id} href={productLink(product)} show={open} onHide={() => setOpen(false)}/>
    </div>;
}
